import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/default.css";
import "../styles/market.css";
import "../styles/responsive.css";
import "../styles/tailwind-bridge.css";
import "../styles/home.css";
import Footer from "./common/Footer.jsx";
import Navbar from "./common/Navbar.jsx";

/* ────Scroll-reveal hook
  Add the `animate-slide-up` class on `selector`  once it enters the viewport. ───────────── */
function useScrollReveal(selector = ".scroll-reveal", threshold = 0.12) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-slide-up");
            entry.target.classList.remove("opacity-0");
            // unobserve after first reveal so it stays visible
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -60px 0px" }
    );

    // Small delay so DOM is painted
    const tid = setTimeout(() => {
      document.querySelectorAll(selector).forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(tid);
      observer.disconnect();
    };
  }, [selector, threshold]);
}

/* ──────────── Scroll-progress bar ──── */
function ScrollProgressBar() {
  const barRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const scrolled =
        window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight);
      if (barRef.current) {
        barRef.current.style.width = `${scrolled * 100}%`;
      }
    };
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  return (
    <div ref={barRef} className="scroll-progress-bar" style={{ width: "0%" }} />
  );
}

/* ──────── Parallax helper ───────────── */
function useParallax() {
  useEffect(() => {
    const update = () => {
      const y = window.scrollY;
      document.querySelectorAll(".parallax-slow").forEach((el) => {
        el.style.transform = `translateY(${y * 0.06}px)`;
      });
      document.querySelectorAll(".parallax-medium").forEach((el) => {
        el.style.transform = `translateY(${y * 0.12}px)`;
      });
    };
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
}

/* ───── Typing hero ────────── */
const TypingText = () => {
  const lines = [
    "We Build your business,",
    "We build Africa,",
    "Africa my Business.",
  ];
  const [displayText, setDisplayText] = useState(["", "", ""]);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);

  useEffect(() => {
    if (currentLine >= lines.length) return;
    if (currentChar < lines[currentLine].length) {
      const t = setTimeout(() => {
        setDisplayText((prev) => {
          const n = [...prev];
          n[currentLine] = lines[currentLine].substring(0, currentChar + 1);
          return n;
        });
        setCurrentChar((c) => c + 1);
      }, 30);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setCurrentLine((l) => l + 1);
        setCurrentChar(0);
      }, 500);
      return () => clearTimeout(t);
    }
  }, [currentLine, currentChar]);

  return (
    <section className="relative h-[300px] md:h-[500px] overflow-hidden rounded-2xl mx-2 hero-section">
      <div className="absolute inset-0">
        <img
          src="images/market.jpg"
          alt="IBloTec Market"
          className="w-full h-full object-cover parallax-slow"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3c3f8c]/80 via-[#00a6d8]/50 to-[#3c3f8c]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000]/50 to-[#000000]/50" />
      </div>

      {/* Floating orbs */}
      <div
        className="absolute top-8 right-12 w-24 h-24 bg-[#00a6d8]/20 rounded-full blur-xl animate-float"
        style={{ animationDelay: "0s" }}
      />
      <div
        className="absolute bottom-10 left-1/3 w-16 h-16 bg-white/10 rounded-full blur-lg animate-float"
        style={{ animationDelay: "2s" }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-8 h-8 bg-[#00a6d8]/30 rounded-full animate-float"
        style={{ animationDelay: "1s" }}
      />

      <div className="relative z-10 h-full flex items-center justify-start px-2 md:px-12">
        <div className="text-white">
          <div className="text-3xl md:text-7xl font-bold leading-normal md:leading-tight">
            {displayText.map((line, index) => (
              <React.Fragment key={index}>
                <span
                  className="block text-white"
                  style={{
                    opacity: line ? 1 : 0,
                    transition: "opacity 0.3s ease",
                    textShadow: "0 2px 20px rgba(0,166,216,0.4)",
                  }}
                >
                  {line}
                  {currentLine === index &&
                    currentChar < lines[index].length && (
                      <span className="inline-block w-1 h-[0.9em] bg-[#00a6d8] ml-1 align-middle animate-pulse" />
                    )}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ────── Achievements showcase ──── */
const AchievementsShowcase = () => {
  const [counts, setCounts] = useState({
    sellers: 0,
    trained: 0,
    agents: 0,
    users: 0,
  });

  useEffect(() => {
    const targets = { sellers: 200, trained: 5000, agents: 50, users: 1000 };
    const steps = 60;
    const duration = 2000;
    let step = 0;
    const t = setInterval(() => {
      if (step < steps) {
        const eased = 1 - Math.pow(1 - (step + 1) / steps, 3); // ease-out-cubic
        setCounts({
          sellers: Math.floor(targets.sellers * eased),
          trained: Math.floor(targets.trained * eased),
          agents: Math.floor(targets.agents * eased),
          users: Math.floor(targets.users * eased),
        });
        step++;
      } else {
        setCounts(targets);
        clearInterval(t);
      }
    }, duration / steps);
    return () => clearInterval(t);
  }, []);

  const cards = [
    {
      key: "sellers",
      value: counts.sellers,
      suffix: "+",
      label: "Active Sellers",
      sub: "Across Africa",
      from: "from-amber-400",
      to: "to-orange-500",
      icon: "fa-solid fa-store",
      border: "border-amber-200/50",
      text: "from-amber-600 to-orange-600",
    },
    {
      key: "trained",
      value: counts.trained,
      suffix: "+",
      label: "Youth Trained",
      sub: "IBloTec Academy",
      from: "from-emerald-400",
      to: "to-teal-500",
      icon: "fa-solid fa-graduation-cap",
      border: "border-emerald-200/50",
      text: "from-emerald-600 to-teal-600",
    },
    {
      key: "agents",
      value: counts.agents,
      suffix: "+",
      label: "Agents Network",
      sub: "All Communes",
      from: "from-blue-400",
      to: "to-indigo-500",
      icon: "fa-solid fa-users",
      border: "border-blue-200/50",
      text: "from-blue-600 to-indigo-600",
    },
    {
      key: "users",
      value: counts.users,
      suffix: "K+",
      label: "Global Users",
      sub: "And Growing",
      from: "from-purple-400",
      to: "to-pink-500",
      icon: "fa-solid fa-globe",
      border: "border-purple-200/50",
      text: "from-purple-600 to-pink-600",
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
      {cards.map((c, i) => (
        <div
          key={c.key}
          className={`achievement-card scroll-reveal relative group`}
          style={{ transitionDelay: `${i * 0.12}s` }}
        >
          <div
            className={`absolute inset-0 bg-gradient-to-r ${c.from} ${c.to} rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-500`}
          />
          <div
            className={`relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border ${c.border} hover:scale-105 transition-all duration-500 hover-lift`}
          >
            <div
              className={`w-12 h-12 bg-gradient-to-br ${c.from} ${c.to} rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:rotate-12 transition-transform icon-spin-hover`}
            >
              <i className={`${c.icon} text-white text-xl`} />
            </div>
            <div className="text-center">
              <div
                className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${c.text} bg-clip-text text-transparent`}
              >
                {c.value === 10000 ? "10" : c.value}
                {c.suffix}
              </div>
              <div className="text-sm text-gray-600 font-semibold mt-1">
                {c.label}
              </div>
              <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ───── Ecosystem section ───────── */
const EcosystemSection = () => (
  <section className="py-24 bg-gradient-to-br from-white via-indigo-50 to-blue-50 scroll-reveal reveal-blur opacity-0 relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#00a6d8]/5 rounded-full blur-3xl animate-float" />
      <div
        className="absolute bottom-20 right-10 w-96 h-96 bg-[#3c3f8c]/5 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "2s" }}
      />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#00a6d8]/5 to-[#3c3f8c]/5 rounded-full blur-3xl animate-pulse-slow" />
      {/* Dot grid */}
      <div className="absolute inset-0 dot-grid-bg opacity-40" />
    </div>

    <div className="container-iblotec relative z-10">
      <div className="text-center mb-16">
        <div className="section-badge scroll-reveal inline-flex items-center space-x-2 bg-gradient-to-r from-[#00a6d8] to-[#3c3f8c] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 animate-bounce-subtle">
          <i className="fa-solid fa-crown text-white" />
          <span className="text-white">The IBloTec Ecosystem</span>
        </div>
        <h2 className="section-heading scroll-reveal text-4xl md:text-6xl font-black mb-6">
          <span className="bg-gradient-to-r from-[#3c3f8c] via-[#00a6d8] to-[#3c3f8c] bg-clip-text text-transparent">
            By Africans, For Africans,
          </span>
          <br />
          <span className="text-[#00a6d8]">Serving the World</span>
        </h2>
        <p
          className="section-heading scroll-reveal text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed"
          style={{ transitionDelay: "0.1s" }}
        >
          A visionary tech company designed to connect people, products, and
          innovation uniting African cultures, traditions, and technologies
          across the continent and diaspora.
        </p>
      </div>

      {/* Ecosystem Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        {[
          {
            accent: "from-emerald-400 to-teal-500",
            border: "border-emerald-100 hover:border-emerald-300",
            pulse: "bg-emerald-500",
            mainIcon: "fa-solid fa-cart-shopping",
            badgeIcon: "fa-solid fa-store",
            title: "IBloTec Market",
            desc: (
              <>
                <span className="font-bold text-emerald-600">AfroMarket:</span>{" "}
                Empowering local businesses, potentials, and talents to export
                African ideas to the world.
              </>
            ),
            checks: [
              "Empower small businesses",
              "Promote AfroCommerce",
              "Celebrate African cultures & traditions",
              "Made in Burundi → Made for the World",
            ],
            checkColor: "text-emerald-500",
          },
          {
            accent: "from-blue-400 to-indigo-500",
            border: "border-blue-100 hover:border-blue-300",
            pulse: "bg-blue-500",
            pulseDelay: "1s",
            mainIcon: "fa-solid fa-coins",
            badgeIcon: "fa-solid fa-bolt",
            title: "IBloCash & IBloPay",
            desc: "Revolutionary fintech solutions that empower people and strengthen financial systems across Africa.",
            grid: [
              { icon: "fa-solid fa-wallet", label: "Digital Wallet" },
              { icon: "fa-solid fa-mobile-alt", label: "Mobile Payments" },
              {
                icon: "fa-solid fa-building-columns",
                label: "Bank Integration",
              },
              { icon: "fa-solid fa-globe", label: "International" },
            ],
            gridColor: "text-blue-500",
          },
          {
            accent: "from-purple-400 to-pink-500",
            border: "border-purple-100 hover:border-purple-300",
            pulse: "bg-purple-500",
            pulseDelay: "2s",
            mainIcon: "fa-solid fa-message",
            badgeIcon: "fa-solid fa-comments",
            title: "IBloChat & Innovation",
            desc: "Social communication and creative tools that tell real African stories and preserve our heritage.",
            quote:
              '"Innovating products that serve people, from communication to civilization — telling Burundi\'s real stories to the world."',
          },
        ].map((card, i) => (
          <div
            key={i}
            className={`ecosystem-card scroll-reveal group relative`}
            style={{ transitionDelay: `${i * 0.15}s` }}
          >
            <div
              className={`absolute inset-0 bg-gradient-to-r ${card.accent} rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
            />
            <div
              className={`relative bg-white rounded-3xl p-8 shadow-2xl border ${card.border} transition-all duration-500 hover:scale-105`}
            >
              <div
                className={`absolute -top-4 -right-4 w-12 h-12 ${card.pulse} rounded-full flex items-center justify-center animate-pulse`}
                style={
                  card.pulseDelay ? { animationDelay: card.pulseDelay } : {}
                }
              >
                <i className={`${card.badgeIcon} text-white text-2xl`} />
              </div>
              <div
                className={`w-16 h-16 bg-gradient-to-br ${card.accent} rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform icon-spin-hover`}
              >
                <i className={`${card.mainIcon} text-white text-3xl`} />
              </div>
              <h3 className="text-2xl font-bold text-[#3c3f8c] mb-4">
                {card.title}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">{card.desc}</p>
              {card.checks && (
                <div className="space-y-2">
                  {card.checks.map((c, j) => (
                    <div
                      key={j}
                      className="flex items-center text-sm text-gray-600"
                    >
                      <i
                        className={`fa-solid fa-check-circle ${card.checkColor} mr-2`}
                      />
                      <span>{c}</span>
                    </div>
                  ))}
                </div>
              )}
              {card.grid && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {card.grid.map((g, j) => (
                    <div key={j} className="flex items-center text-gray-600">
                      <i className={`${g.icon} ${card.gridColor} mr-1`} />
                      <span>{g.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {card.quote && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                  <p className="text-sm text-gray-700 italic">{card.quote}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Academy */}
      <div className="academy-section scroll-reveal relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
        <div className="relative bg-white rounded-3xl p-10 shadow-2xl border border-amber-200 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-400/10 to-orange-500/10 rounded-full -mr-20 -mt-20" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform icon-spin-hover">
                <i className="fa-solid fa-graduation-cap text-white text-2xl" />
              </div>
              <h3 className="text-3xl font-bold text-[#3c3f8c] mb-4">
                IBloTec Academy
              </h3>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Empowering thousands of young Africans to discover their
                potential, teaching the new world economy in a practical way,
                and creating job opportunities through digital skills and
                innovation.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full border-2 border-white flex items-center justify-center"
                    >
                      <i className="fa-solid fa-user text-white text-xs" />
                    </div>
                  ))}
                </div>
                <span className="text-sm text-gray-600">+5,000 graduates</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  v: "40+",
                  l: "Communities",
                  colors: "from-amber-50 to-orange-50",
                  tv: "text-amber-600",
                },
                {
                  v: "100%",
                  l: "Practical Training",
                  colors: "from-orange-50 to-red-50",
                  tv: "text-orange-600",
                },
                {
                  v: "20+",
                  l: "Courses",
                  colors: "from-yellow-50 to-amber-50",
                  tv: "text-yellow-600",
                },
                {
                  v: "∞",
                  l: "Opportunities",
                  colors: "from-red-50 to-pink-50",
                  tv: "text-red-600",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className={`bg-gradient-to-br ${s.colors} rounded-xl p-4 text-center hover:scale-105 transition-transform duration-300`}
                >
                  <div className={`text-3xl font-bold ${s.tv} mb-1`}>{s.v}</div>
                  <div className="text-xs text-gray-600">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────── Vision 2029 ──────────────── */
const Vision2029 = () => {
  const visionItems = [
    {
      icon: "fa-solid fa-microchip",
      title: "Africa Tech Tycoon",
      desc: "Leading Africa's digital transformation with innovative solutions",
      color: "from-cyan-400 to-blue-500",
    },
    {
      icon: "fa-solid fa-trophy",
      title: "Market Dominance",
      desc: "Setting the standard for AfroCommerce across the continent",
      color: "from-yellow-400 to-amber-500",
    },
    {
      icon: "fa-solid fa-hand-holding-heart",
      title: "Service Humanity",
      desc: "Using technology to improve lives and create opportunities",
      color: "from-pink-400 to-rose-500",
    },
    {
      icon: "fa-solid fa-briefcase",
      title: "5,000+ Workforce",
      desc: "Creating meaningful employment across Africa and beyond",
      color: "from-green-400 to-emerald-500",
    },
    {
      icon: "fa-solid fa-users",
      title: "Millions of Users",
      desc: "Connecting Africa and the diaspora on a global scale",
      color: "from-purple-400 to-indigo-500",
    },
    {
      icon: "fa-solid fa-chart-line",
      title: "Continental Leader",
      desc: "Shaping policies and innovations for African tech ecosystem",
      color: "from-red-400 to-orange-500",
    },
  ];

  const timeline = [
    {
      year: "2021",
      text: "IBloTec founded — A dream to transform Africa through technology",
      color: "text-[#00a6d8]",
      dotBg: "bg-[#00a6d8]",
      side: "left",
    },
    {
      year: "2023",
      text: "IBloTec Market launch — Connecting African sellers to the world",
      color: "text-[#3c3f8c]",
      dotBg: "bg-[#3c3f8c]",
      side: "right",
    },
    {
      year: "2025",
      text: "IBloCash & IBloPay — Building Africa's payment infrastructure",
      color: "text-[#00a6d8]",
      dotBg: "bg-[#00a6d8]",
      side: "left",
    },
    {
      year: "2029",
      text: "Africa's leading tech ecosystem — 5,000+ workforce, millions of users",
      color: "text-[#3c3f8c]",
      dotBg: "bg-[#3c3f8c]",
      side: "right",
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-tr from-[#171735] to-[#3c3f8c] text-white scroll-reveal reveal-blur opacity-0 relative overflow-hidden">
      {/* Stars */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-twinkle"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="container-iblotec relative z-10">
        <div className="text-center mb-16">
          <div className="section-badge scroll-reveal inline-flex items-center space-x-2 bg-gradient-to-r from-white/40 to-white text-black px-6 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fa-solid fa-eye text-[#3c3f8c]" />
            <span className="text-[#3c3f8c]">IBloTec 2029 Vision</span>
          </div>
          <h2 className="section-heading scroll-reveal text-5xl md:text-7xl font-black mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00a6d8] via-amber-100 to-[#00a6d8]">
              Where We're Headed
            </span>
          </h2>
          <p
            className="section-heading scroll-reveal text-xl text-white/80 max-w-3xl mx-auto"
            style={{ transitionDelay: "0.1s" }}
          >
            Since 2021, we've never stopped dreaming. Despite challenges, we
            keep building Africa's digital future.
          </p>
        </div>

        {/* Vision cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {visionItems.map((item, i) => (
            <div
              key={i}
              className={`vision-card scroll-reveal group relative`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
              />
              <div className="relative bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-white/30 transition-all duration-500 hover:scale-105 hover:bg-white/10">
                <div
                  className={`w-16 h-16 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 icon-spin-hover`}
                >
                  <i className={`${item.icon} text-white text-2xl`} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">
                  {item.title}
                </h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#00a6d8] to-[#3c3f8c] hidden md:block" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {timeline.map((t, i) => (
              <div
                key={i}
                className={`timeline-item scroll-reveal relative ${
                  t.side === "right" ? "md:col-start-2" : "md:text-right"
                }`}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-white/30 transition-all duration-500 hover-lift">
                  <div
                    className={`absolute ${
                      t.side === "left" ? "-right-4" : "-left-4"
                    } top-1/2 transform -translate-y-1/2 w-8 h-8 ${
                      t.dotBg
                    } rounded-full hidden md:flex items-center justify-center`}
                  >
                    <div className="w-3 h-3 bg-white rounded-full animate-ping" />
                  </div>
                  <h4 className={`text-2xl font-bold ${t.color} mb-2`}>
                    {t.year}
                  </h4>
                  <p className="text-white/80">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────  Product slider ───────── */
const ProductSlider = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/products`);
        if (!res.ok) throw new Error("Failed to fetch products");
        const data = await res.json();
        setProducts(data.slice(0, 10));
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= products.length - 3 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [products.length]);

  if (loading) {
    return (
      <section className="py-24 bg-gradient-to-br from-[#3c3f8c] to-[#00a6d8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="images/market1.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-iblotec text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-[#00a6d8] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fa-solid fa-shopping-bag" />
            <span>Featured Products</span>
          </div>
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-[#00a6d8]/30 border-t-[#00a6d8] rounded-full animate-spin" />
              <div
                className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-white rounded-full animate-spin"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              />
            </div>
            <p className="text-xl text-white">Loading amazing products...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 bg-gradient-to-br from-[#3c3f8c] to-[#00a6d8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="images/market.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-iblotec text-center relative z-10">
          <div className="bg-[#3c3f8c]/20 border border-white/20 rounded-3xl md:p-12 max-w-md mx-auto backdrop-blur-sm cta-block scroll-reveal">
            <Link
              to="https://iblotecmarket.com/vendor/auth/registration/index"
              className="border-2 border-white text-white px-10 py-4 rounded-full font-bold text-lg bg-white/70 backdrop-blur-sm hover:bg-white transition-all duration-300 flex items-center justify-center space-x-3 hover:scale-105 hover:shadow-2xl btn-press"
            >
              <span className="text-[#00a6d8]">START SHOPPING NOW</span>
              <i className="fa-solid fa-shopping-cart text-[#00a6d8]" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const visibleProducts = products.slice(currentIndex, currentIndex + 3);

  return (
    <section className="py-24 bg-gradient-to-br from-[#3c3f8c] to-[#00a6d8] text-white relative overflow-hidden scroll-reveal reveal-blur opacity-0">
      <div className="absolute inset-0 opacity-30">
        <img
          src="images/market.jpg"
          alt=""
          className="w-full h-full object-cover parallax-slow"
        />
      </div>
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#00a6d8]/10 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-20 right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="container-iblotec relative z-10">
        <div className="text-center mb-16">
          <div className="section-badge scroll-reveal inline-flex items-center space-x-2 bg-[#00a6d8] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fa-solid fa-shopping-bag" />
            <span>Featured Products</span>
          </div>
          <h2 className="section-heading scroll-reveal text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-[#00a6d8] to-white bg-clip-text text-transparent">
              Trending Now
            </span>
          </h2>
          <p
            className="section-heading scroll-reveal text-xl text-white max-w-2xl mx-auto"
            style={{ transitionDelay: "0.1s" }}
          >
            Discover the most popular products from African entrepreneurs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {visibleProducts.map((product, index) => (
            <div
              key={product.id}
              className={`product-card-anim scroll-reveal group`}
              style={{ transitionDelay: `${index * 0.12}s` }}
            >
              <div className="relative overflow-hidden rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 hover:bg-white/20 transition-all duration-500 hover:scale-105">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={`${import.meta.env.VITE_API_URL}/${product.image1}`}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = "images/placeholder.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 right-4">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                      <i className="fa-solid fa-heart text-white text-sm" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-2 text-white group-hover:text-[#00a6d8] transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold text-[#00a6d8]">
                      {product.price} Fbu
                    </p>
                    <button className="bg-[#00a6d8] hover:bg-[#3c3f8c] text-white px-4 py-2 rounded-full text-sm font-semibold hover:scale-105 transition-all duration-300 btn-press">
                      <i className="fa-solid fa-shopping-cart mr-1" />
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center space-x-4 scroll-reveal">
          <button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 disabled:opacity-50 hover:scale-110 btn-press"
            disabled={currentIndex === 0}
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          <div className="flex space-x-2">
            {Array.from({ length: Math.ceil(products.length / 3) }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i * 3)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  Math.floor(currentIndex / 3) === i
                    ? "bg-[#00a6d8] w-8"
                    : "w-3 bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() =>
              setCurrentIndex(Math.min(products.length - 3, currentIndex + 1))
            }
            className="w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 disabled:opacity-50 hover:scale-110 btn-press"
            disabled={currentIndex >= products.length - 3}
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ─────────── Main page ─── */
function Home() {
  // Activate all scroll-reveal listeners
  useScrollReveal(".scroll-reveal", 0.12);
  useScrollReveal(".section-badge", 0.2);
  useScrollReveal(".section-heading", 0.15);
  useScrollReveal(".about-card", 0.15);
  useScrollReveal(".about-image", 0.15);
  useScrollReveal(".ecosystem-card", 0.12);
  useScrollReveal(".academy-section", 0.1);
  useScrollReveal(".achievement-card", 0.15);
  useScrollReveal(".vision-card", 0.12);
  useScrollReveal(".timeline-item", 0.15);
  useScrollReveal(".market-card", 0.12);
  useScrollReveal(".product-card-anim", 0.12);
  useScrollReveal(".cta-block", 0.1);
  useScrollReveal(".stat-badge", 0.2);
  useScrollReveal("footer", 0.05);

  // Parallax
  useParallax();

  return (
    <>
      <ScrollProgressBar />
      <Navbar />

      {/* Hero */}
      <TypingText />

      {/* About IBloTec */}
      <section className="py-10 md:py-24 bg-white scroll-reveal reveal-blur opacity-0 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="images/market1.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dot grid */}
        <div className="absolute inset-0 dot-grid-bg opacity-30" />

        <div className="container-iblotec relative z-10">
          <div className="text-center mb-16">
            <div className="section-badge scroll-reveal inline-flex items-center space-x-2 bg-[#00a6d8] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="fa-solid fa-star text-white" />
              <span className="text-white">About IBloTec</span>
            </div>
            <h2 className="section-heading scroll-reveal text-4xl md:text-6xl font-black mb-6">
              <span className="text-[#3c3f8c]">Building Africa's</span>
              <br />
              <span className="text-[#00a6d8]">Digital Future</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              {[
                {
                  bg: "bg-[#00a6d8]",
                  icon: "fa-solid fa-lightbulb",
                  title: "Our Vision",
                  body: (
                    <>
                      <strong>IBloTec</strong> is a visionary African tech
                      company committed to building a complete digital ecosystem
                      that connects people, products, and innovation across
                      Africa and the diaspora.
                    </>
                  ),
                },
                {
                  bg: "bg-[#3c3f8c]",
                  icon: "fa-solid fa-rocket",
                  title: "Our Mission",
                  body: (
                    <>
                      Founded in 2021, IBloTec stands for{" "}
                      <span className="font-semibold text-[#00a6d8]">
                        Innovation, Blockchain, and Technology
                      </span>{" "}
                      — driving financial inclusion, empowering local
                      businesses, and shaping Africa's future through
                      technology.
                    </>
                  ),
                },
                {
                  bg: "bg-[#00a6d8]",
                  icon: "fa-solid fa-users",
                  title: "By Africans, For Africans",
                  body: (
                    <>
                      From <strong>IBloTec Market</strong> to upcoming
                      innovations like <em>IBlox</em>, <em>IBloCash</em>,{" "}
                      <em>IBloChat</em>, and <em>IBloTec Academy</em>, we create
                      tools made by Africans, for Africans.
                    </>
                  ),
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="about-card scroll-reveal bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-[#00a6d8]/20 hover-lift"
                  style={{ transitionDelay: `${i * 0.15}s` }}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center flex-shrink-0 icon-spin-hover`}
                    >
                      <i className={`${card.icon} text-white text-xl`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#3c3f8c] mb-3">
                        {card.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {card.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="about-image scroll-reveal relative">
              <div className="relative z-10">
                <img
                  src="images/market1.jpg"
                  alt="IBloTec Vision"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl" />
                <div className="absolute bottom-8 left-8 text-white">
                  <h4 className="text-2xl font-bold mb-2 text-white">
                    2025 - Present
                  </h4>
                  <p className="text-lg opacity-90 text-white">
                    Building Africa's Digital Ecosystem
                  </p>
                </div>
              </div>
              <div
                className="stat-badge scroll-reveal absolute -top-6 -right-6 bg-white rounded-2xl p-6 shadow-xl border border-[#00a6d8]/20 z-10"
                style={{ transitionDelay: "0.3s" }}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00a6d8]">1000+</div>
                  <div className="text-sm text-[#3c3f8c]">Active Users</div>
                </div>
              </div>
              <div
                className="stat-badge scroll-reveal absolute -bottom-20 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-[#00a6d8]/20 z-10"
                style={{ transitionDelay: "0.45s" }}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#3c3f8c]">2+</div>
                  <div className="text-sm text-[#3c3f8c]">
                    Countries Reached
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem */}
      <EcosystemSection />

      {/* IBloTec Market */}
      <section className="py-24 bg-gradient-to-br from-[#3c3f8c]/80 to-[#00a6d8]/80 text-white scroll-reveal reveal-blur opacity-0 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="images/market1.jpg"
            alt=""
            className="w-full h-full object-cover parallax-slow"
          />
        </div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#00a6d8]/50 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="container-iblotec relative z-10">
          <div className="text-center mb-16">
            <div className="section-badge scroll-reveal inline-flex items-center space-x-2 bg-[#00a6d8] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="fa-solid fa-store text-white" />
              <span className="text-white">Our Flagship Platform</span>
            </div>
            <h2 className="section-heading scroll-reveal text-5xl md:text-7xl font-black mb-6">
              <span className="text-white">IBloTec Market</span>
            </h2>
            <p
              className="section-heading scroll-reveal text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed"
              style={{ transitionDelay: "0.1s" }}
            >
              Powering African Commerce • Connecting Continents • Building
              Dreams
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                bg: "bg-[#00a6d8]/60",
                icon: "fa-solid fa-globe-africa",
                titleColor: "text-[#c7f2ff]",
                title: "Digital Inclusion",
                body: "Giving even small, rural vendors access to e-commerce and global markets through innovative technology.",
              },
              {
                bg: "bg-[#3c3f8c]/70",
                icon: "fa-solid fa-heart",
                titleColor: "text-[#cdceff]",
                title: "Afro-centric Trade",
                body: "Promoting 'Made in Africa' products with pride, celebrating our heritage and craftsmanship.",
              },
              {
                bg: "bg-[#00a6d8]/50",
                icon: "fa-solid fa-briefcase",
                titleColor: "text-[#c7f2ff]",
                title: "Opportunity Creation",
                body: "Creating jobs for agents, delivery workers, and digital marketers across the continent.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className={`market-card scroll-reveal group`}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 hover:scale-105 hover-lift">
                  <div
                    className={`w-16 h-16 ${c.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 icon-spin-hover`}
                  >
                    <i className={`${c.icon} text-white text-2xl`} />
                  </div>
                  <h3 className={`text-2xl font-bold mb-4 ${c.titleColor}`}>
                    {c.title}
                  </h3>
                  <p className="text-white leading-relaxed">{c.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="cta-block scroll-reveal text-center">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl py-10 px-5 md:p-16 border border-white/20 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-4xl font-bold mb-6 text-white">
                Ready to Transform African Commerce?
              </h3>
              <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
                Join thousands of entrepreneurs who are already building their
                digital future with IBloTec Market.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/https://iblotecmarket.com/"
                  className="group bg-[#00a6d8] hover:bg-[#3c3f8c] text-white md:px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center justify-center space-x-3 btn-press"
                >
                  <span className="text-white">Start Shopping</span>
                  <i className="fa-solid fa-shopping-cart group-hover:bounce text-white" />
                </Link>
                <Link
                  to="https://iblotecmarket.com/vendor/auth/registration/index"
                  className="border-2 border-white text-white md:px-10 py-4 rounded-full font-bold text-lg bg-white/70 backdrop-blur-sm hover:bg-white transition-all duration-300 flex items-center justify-center space-x-3 hover:scale-105 hover:shadow-2xl btn-press"
                >
                  <span className="text-[#00a6d8]">Become a Seller</span>
                  <i className="fa-solid fa-store text-[#00a6d8]" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-16 bg-gradient-to-br from-white to-gray-50">
        <div className="container-iblotec">
          <AchievementsShowcase />
        </div>
      </section>

      {/* Vision 2029 */}
      <Vision2029 />

      {/* Product Slider */}
      <ProductSlider />

      <Footer />
    </>
  );
}

export default Home;
